import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import styles from "./NuevoProducto.module.scss";
import {
  openMainModal,
  closeMainModal,
  setPortfolio,
} from "../../../../../store/actions";
import { X } from "lucide-react";
import NuevoProductoModal from "./NuevoProductoModal.tsx";
import ModalOverlay from "../../../../UI/ModalOverlay/ModalOverlay.tsx";

interface PortfolioFormValues {
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  zone: string;
  type: string;
  priceUnit: string;
  price: string;
  currencyType: string;
}

interface NuevoProductoProps {
  setPortfolio: (values: PortfolioFormValues, images: File[], context: string) => void;
  openMainModal: (config: any) => void;
  cancel: () => void;
  closeMainModal: () => void;
}

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { delay: 0.1, duration: 0.3, ease: "easeOut" },
  },
};

const NuevoProducto: React.FC<NuevoProductoProps> = ({
  setPortfolio,
  openMainModal,
  cancel,
  closeMainModal,
}) => {
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState<PortfolioFormValues>({
    name: "",
    startDate: "",
    endDate: "",
    description: "",
    zone: "",
    type: "",
    priceUnit: "",
    price: "",
    currencyType: "",
  });

  const validateForm = (data: PortfolioFormValues, images: File[]): boolean => {
    if (
      !data.name.trim() ||
      !data.description.trim() ||
      !data.startDate ||
      !data.endDate ||
      !data.zone.trim() ||
      images.length === 0 ||
      !data.type ||
      !data.currencyType
    ) {
      return false;
    }

    if (new Date(data.startDate) > new Date(data.endDate)) {
      return false;
    }

    return true;
  };

  const isValid = useMemo(() => {
    return validateForm(formData, imageFiles);
  }, [formData, imageFiles]);

  const handleFieldChange = (
    field: keyof PortfolioFormValues,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (newImages: File[]) => {
    setImageFiles(newImages);
  };

  const createHandler = () => {
    openMainModal({
      title: `¿Estas seguro que deseas crear el ${
        formData.type === "producto" ? "producto" : "servicio"
      } en edify?`,
      iconType: "warning",
      body: "Una vez creado será publicado en edify y podrá ser visto por toda la comunidad.",
      isSuccess: false,
      confirmHandler: () => {
        cancel();
        setPortfolio(formData, imageFiles, "perfil");
        closeMainModal();
      },
    });
  };

  return (
    <ModalOverlay isOpen={true} onClose={cancel}>
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className={styles.newPortfolioModal}
      >
        <motion.div
          initial={{ height: "0" }}
          animate={{ height: "auto" }}
          exit={{ height: "0" }}
          transition={{ damping: 5, height: { duration: 0.5 } }}
          className={styles.container}
        >
          <header className={styles.container_header}>
            <div className={styles.container_header_txt}>
              Crear nuevo producto o servicio
            </div>
            <div className={styles.container_header_cancel} onClick={cancel}>
              <X size={24} />
            </div>
          </header>
          <NuevoProductoModal
            formData={formData}
            onFieldChange={handleFieldChange}
            onImageChange={handleImageChange}
            imageFiles={imageFiles}
          />
          <div className={styles.container_footer}>
            <button className={styles.container_cancel} onClick={cancel}>
              Cancelar
            </button>
            <button
              className={`${styles.container_submit} ${
                !isValid ? styles.disabled : ""
              }`}
              disabled={!isValid}
              onClick={createHandler}
            >
              Publicar en mi perfil
            </button>
          </div>
        </motion.div>
      </motion.div>
    </ModalOverlay>
  );
};

export default connect(null, {
  openMainModal,
  setPortfolio,
  closeMainModal,
})(NuevoProducto);
